import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { ScheduleAuditLog } from "../../../../types";
import { AuditEntry } from "./AuditEntry";
import { formatDayHeading, formatTime, getActionMeta, getDayKey } from "./auditUtils";

export interface AuditTimelineProps {
    logs: ScheduleAuditLog[];
    timezone?: string;
    /** group by day with headings (period view) vs. flat list (single schedule) */
    groupByDay?: boolean;
    showEmployee?: boolean;
    onViewHistory?: (log: ScheduleAuditLog) => void;
}

type Node =
    | { kind: "entry"; log: ScheduleAuditLog }
    | { kind: "batch"; batchId: string; logs: ScheduleAuditLog[] };

/** Collapse rows sharing a batch_id (publish / copy / mass delete) into one node, keeping order. */
const buildNodes = (logs: ScheduleAuditLog[]): Node[] => {
    const counts = new Map<string, number>();
    logs.forEach((l) => l.batch_id && counts.set(l.batch_id, (counts.get(l.batch_id) || 0) + 1));

    const nodes: Node[] = [];
    const batchNodes = new Map<string, Extract<Node, { kind: "batch" }>>();
    logs.forEach((log) => {
        const id = log.batch_id;
        if (id && (counts.get(id) || 0) > 1) {
            const existing = batchNodes.get(id);
            if (existing) {
                existing.logs.push(log);
            } else {
                const node = { kind: "batch" as const, batchId: id, logs: [log] };
                batchNodes.set(id, node);
                nodes.push(node);
            }
        } else {
            nodes.push({ kind: "entry", log });
        }
    });
    return nodes;
};

const BatchGroup: React.FC<{
    logs: ScheduleAuditLog[];
    timezone?: string;
    isLast: boolean;
    onViewHistory?: (log: ScheduleAuditLog) => void;
}> = ({ logs, timezone, isLast, onViewHistory }) => {
    const [open, setOpen] = useState(false);
    const request = logs.find((l) => l.action === "publish_request");
    const children = logs.filter((l) => l !== request);
    const head = request || logs[0];
    const meta = getActionMeta(head.action, head.action_label);
    const title = request
        ? `${meta.label} · ${children.length} shift${children.length === 1 ? "" : "s"}`
        : `${meta.label} · ${logs.length} shifts`;

    return (
        <div className={`relative flex gap-3 ${isLast ? "" : "pb-5"}`}>
            <div className="flex flex-col items-center">
                <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${meta.dot}`} />
                {!isLast && <span className="mt-1 w-px flex-1 bg-gray-200" />}
            </div>
            <div className="min-w-0 flex-1">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="flex w-full items-start justify-between gap-2 text-left"
                    aria-expanded={open}
                >
                    <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900">{title}</span>
                        <span className="block text-xs text-gray-500">
                            by {head.actor_name || head.actor_id || "Unknown"} · {formatTime(head.created, timezone)}
                        </span>
                    </span>
                    <span className="mt-0.5 flex items-center gap-1 text-xs text-blue-600">
                        {open ? "Hide" : "Show"}
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                </button>
                {open && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
                        {request && <AuditEntry log={request} timezone={timezone} isLast={children.length === 0} />}
                        {children.map((log, i) => (
                            <AuditEntry
                                key={log.os_schedule_audit_log_id}
                                log={log}
                                timezone={timezone}
                                showEmployee
                                onViewHistory={onViewHistory}
                                isLast={i === children.length - 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const NodeList: React.FC<{
    logs: ScheduleAuditLog[];
    timezone?: string;
    showEmployee: boolean;
    onViewHistory?: (log: ScheduleAuditLog) => void;
}> = ({ logs, timezone, showEmployee, onViewHistory }) => {
    const nodes = useMemo(() => buildNodes(logs), [logs]);
    return (
        <div>
            {nodes.map((node, i) =>
                node.kind === "batch" ? (
                    <BatchGroup
                        key={`b-${node.batchId}`}
                        logs={node.logs}
                        timezone={timezone}
                        isLast={i === nodes.length - 1}
                        onViewHistory={onViewHistory}
                    />
                ) : (
                    <AuditEntry
                        key={node.log.os_schedule_audit_log_id}
                        log={node.log}
                        timezone={timezone}
                        showEmployee={showEmployee}
                        onViewHistory={onViewHistory}
                        isLast={i === nodes.length - 1}
                    />
                )
            )}
        </div>
    );
};

export const AuditTimeline: React.FC<AuditTimelineProps> = ({
    logs,
    timezone,
    groupByDay = false,
    showEmployee = false,
    onViewHistory,
}) => {
    const days = useMemo(() => {
        if (!groupByDay) return [];
        const map = new Map<string, ScheduleAuditLog[]>();
        logs.forEach((log) => {
            const key = getDayKey(log.created, timezone);
            map.set(key, [...(map.get(key) || []), log]);
        });
        return Array.from(map.entries());
    }, [logs, groupByDay, timezone]);

    if (!groupByDay) {
        return <NodeList logs={logs} timezone={timezone} showEmployee={showEmployee} onViewHistory={onViewHistory} />;
    }

    return (
        <div className="space-y-6">
            {days.map(([dayKey, dayLogs]) => (
                <section key={dayKey}>
                    <h4 className="sticky top-0 z-10 -mx-4 mb-3 bg-white/95 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500 backdrop-blur">
                        {formatDayHeading(dayKey)}
                        <span className="ml-2 font-normal normal-case text-gray-400">{dayLogs.length}</span>
                    </h4>
                    <NodeList logs={dayLogs} timezone={timezone} showEmployee={showEmployee} onViewHistory={onViewHistory} />
                </section>
            ))}
        </div>
    );
};

export default AuditTimeline;
